import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CircleNotch, Plus, X, Lightning, Warning, UsersThree, Wallet, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { customColor } from "@/lib/planiq";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import Logomark from "@/components/Logomark";

const KICKER = "text-xs tracking-[0.25em] uppercase font-bold text-gray-500";

const CONSTRAINT_GROUPS = [
  { key: "operational_hazards", label: "Operational Hazards", icon: Warning },
  { key: "cultural_friction", label: "Cultural Friction", icon: UsersThree },
  { key: "resource_limits", label: "Resource Limits", icon: Wallet },
];

// Textarea that grows to fit its content so nothing is clipped.
function AutoTextarea({ value, onChange, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      className="flex-1 resize-none overflow-hidden border border-gray-200 rounded-none px-3 py-2 text-sm leading-relaxed text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...props}
    />
  );
}

export default function Blueprint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [constraints, setConstraints] = useState({ operational_hazards: [], cultural_friction: [], resource_limits: [] });
  const [pillars, setPillars] = useState([]);
  const [customName, setCustomName] = useState("");
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const tick = async () => {
      const p = await api.getPlan(id).catch(() => null);
      if (!p) return;
      if (p.status === "ready" || p.status === "generating") {
        clearInterval(pollRef.current);
        navigate(`/plan/${id}`, { replace: true });
        return;
      }
      if (p.status === "error") {
        clearInterval(pollRef.current);
        toast.error("Analysis failed. Please start again.");
        return;
      }
      if (p.status === "blueprint_ready" && p.blueprint) {
        clearInterval(pollRef.current);
        setPlan(p);
        setConstraints({
          operational_hazards: p.blueprint.inferred_constraints?.operational_hazards || [],
          cultural_friction: p.blueprint.inferred_constraints?.cultural_friction || [],
          resource_limits: p.blueprint.inferred_constraints?.resource_limits || [],
        });
        setPillars((p.blueprint.suggested_pillars || []).map((pl) => ({ ...pl, selected: true })));
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => clearInterval(pollRef.current);
  }, [id, navigate]);

  const editConstraint = (group, idx, val) =>
    setConstraints((c) => ({ ...c, [group]: c[group].map((v, i) => (i === idx ? val : v)) }));
  const removeConstraint = (group, idx) =>
    setConstraints((c) => ({ ...c, [group]: c[group].filter((_, i) => i !== idx) }));
  const addConstraint = (group) =>
    setConstraints((c) => ({ ...c, [group]: [...c[group], ""] }));

  const togglePillar = (pid) =>
    setPillars((prev) => prev.map((p) => (p.id === pid ? { ...p, selected: !p.selected } : p)));
  const editPillarName = (pid, name) =>
    setPillars((prev) => prev.map((p) => (p.id === pid ? { ...p, name } : p)));
  const removePillar = (pid) => setPillars((prev) => prev.filter((p) => p.id !== pid));

  const addCustomPillar = async () => {
    const name = customName.trim();
    if (!name) return;
    const idx = pillars.length;
    const pid = `custom_${Date.now()}`;
    setPillars((prev) => [
      ...prev,
      {
        id: pid,
        name,
        rationale: "",
        color_key: `dyn_${idx}`,
        hex: customColor(idx),
        is_custom: true,
        selected: true,
        elaborating: true,
      },
    ]);
    setCustomName("");
    try {
      const { description } = await api.elaborate(id, name, "pillar");
      setPillars((prev) => prev.map((p) =>
        p.id === pid ? { ...p, rationale: description || "Custom pillar.", elaborating: false } : p));
    } catch (e) {
      setPillars((prev) => prev.map((p) =>
        p.id === pid ? { ...p, rationale: "Custom pillar.", elaborating: false } : p));
    }
  };

  const generate = async () => {
    const chosen = pillars.filter((p) => p.selected && p.name.trim());
    if (chosen.length === 0) return toast.error("Keep at least one pillar.");
    setGenerating(true);
    try {
      const cleaned = {
        operational_hazards: constraints.operational_hazards.filter((s) => s.trim()),
        cultural_friction: constraints.cultural_friction.filter((s) => s.trim()),
        resource_limits: constraints.resource_limits.filter((s) => s.trim()),
      };
      const payload = chosen.map((p) => ({
        id: p.id, name: p.name.trim(), color_key: p.color_key, hex: p.hex,
        rationale: p.rationale || "", is_custom: true,
      }));
      await api.generate(id, payload, cleaned);
      navigate(`/plan/${id}`);
    } catch (e) {
      toast.error("Generation failed. Please retry.");
      setGenerating(false);
    }
  };

  if (!plan) {
    return (
      <div className="h-screen flex flex-col items-center justify-center text-center px-6" data-testid="analyzing-state">
        <CircleNotch size={36} className="animate-spin text-gray-900 mb-5" />
        <p className={KICKER + " mb-2"}>Analyzing</p>
        <h1 className="font-display font-black text-3xl tracking-tight mb-3">Reading your context</h1>
        <p className="text-gray-500 max-w-md font-mono text-sm">
          Xartis is extracting the constraints and themes tailored to your plan.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-gray-200 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/start")} className="text-gray-500 hover:text-gray-900"><ArrowLeft size={20} /></button>
          <span className="flex items-center gap-2 shrink-0">
            <Logomark size={24} />
            <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
          </span>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-semibold text-gray-700 truncate">{plan.title}</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <span className="font-mono text-xs text-gray-500">Blueprint</span>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <p className={KICKER}>Confirm before generating</p>
          <h1 className="font-display font-black text-4xl tracking-tight leading-none mt-2 mb-2">Context & Pillar Blueprint</h1>
          <p className="text-base text-gray-600 mb-10 max-w-2xl">
            Xartis parsed your context. Edit anything that's off, deselect any focus areas you don't want, then generate.
          </p>

          {/* Inferred constraints */}
          <section className="mb-12">
            <h2 className="font-display font-bold text-xl tracking-tight mb-4">Inferred Constraints</h2>
            <div className="space-y-5">
              {CONSTRAINT_GROUPS.map(({ key, label, icon: Icon }) => (
                <div key={key} className="border border-gray-200" data-testid={`constraint-group-${key}`}>
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
                    <Icon size={15} weight="duotone" className="text-gray-600" />
                    <h3 className="text-xs tracking-[0.18em] uppercase font-bold text-gray-600">{label}</h3>
                    <span className="ml-auto font-mono text-xs text-gray-400">{constraints[key].length}</span>
                  </div>
                  <div className="p-3 space-y-2">
                    {constraints[key].map((val, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="mt-3 w-1.5 h-1.5 bg-gray-400 shrink-0" />
                        <AutoTextarea
                          data-testid={`constraint-${key}-${i}`}
                          value={val}
                          onChange={(v) => editConstraint(key, i, v)}
                        />
                        <button onClick={() => removeConstraint(key, i)}
                          className="text-gray-400 hover:text-red-600 mt-2.5 shrink-0">
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                    {constraints[key].length === 0 && (
                      <p className="text-xs text-gray-400 font-mono px-1">none inferred</p>
                    )}
                    <button onClick={() => addConstraint(key)} data-testid={`add-constraint-${key}`}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900 pt-1 pl-3.5">
                      <Plus size={13} weight="bold" /> Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Pillars */}
          <section className="mb-10">
            <h2 className="font-display font-bold text-xl tracking-tight mb-1">Suggested Strategic Pillars</h2>
            <p className="text-sm text-gray-500 mb-4">Tailored to this initiative. Every action item will map to one of these.</p>
            <div className="space-y-2">
              {pillars.map((p) => (
                <div key={p.id} data-testid={`pillar-row-${p.id}`}
                  className={`flex items-start gap-3 border p-3 transition-colors ${p.selected ? "border-gray-300 bg-white" : "border-gray-200 bg-gray-50 opacity-60"}`}>
                  <Checkbox checked={p.selected} onCheckedChange={() => togglePillar(p.id)}
                    data-testid={`pillar-checkbox-${p.id}`} className="rounded-none mt-1" />
                  <span className="w-3 h-3 shrink-0 mt-1.5" style={{ backgroundColor: p.hex }} />
                  <div className="flex-1 min-w-0">
                    <input
                      data-testid={`pillar-name-${p.id}`}
                      className="font-semibold text-sm bg-transparent border-b border-transparent hover:border-gray-300 focus:border-gray-900 focus:outline-none w-full"
                      value={p.name}
                      onChange={(e) => editPillarName(p.id, e.target.value)}
                    />
                    {p.elaborating ? (
                      <p className="flex items-center gap-1.5 text-xs text-gray-400 mt-1 font-mono" data-testid={`pillar-elaborating-${p.id}`}>
                        <CircleNotch size={12} className="animate-spin" /> Generating description…
                      </p>
                    ) : p.rationale ? (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{p.rationale}</p>
                    ) : null}
                  </div>
                  <button onClick={() => removePillar(p.id)} className="text-gray-400 hover:text-red-600 mt-0.5 shrink-0">
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 max-w-md mt-4">
              <Input data-testid="input-custom-pillar" className="rounded-none border-gray-300"
                placeholder="Add a custom pillar"
                value={customName} onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustomPillar()} />
              <Button data-testid="add-pillar-button" variant="outline" onClick={addCustomPillar}
                className="rounded-none border-gray-900 shrink-0">
                <Plus size={18} weight="bold" /> Add
              </Button>
            </div>
          </section>

          <div className="flex justify-end pt-6 border-t border-gray-200">
            <Button data-testid="generate-plan-button" disabled={generating} onClick={generate}
              className="rounded-none bg-gray-950 hover:bg-gray-700 px-7 py-6 text-base">
              {generating ? (
                <><CircleNotch size={20} className="animate-spin" /> Building workspace…</>
              ) : (
                <><Lightning size={20} weight="fill" /> Generate Plan</>
              )}
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
